import { createFileRoute, Link } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { businessNode, canonical, SITE_URL } from "@/lib/seo";
import { ogImageMeta } from "@/lib/og-image";
import { Award, Radio, Newspaper, Quote, Phone, ExternalLink } from "lucide-react";

const TITLE =
  "Durafoam Roofing | Rosie on the House Certified Partner Since 1996";
const DESCRIPTION =
  "Durafoam Roofing has been a Rosie on the House Certified Partner since 1996 — 30 years of radio, podcast, and press features on foam roofing in Phoenix. See every appearance in one place.";
const PATH = "/rosie-on-the-house";
const URL = canonical(PATH);

const ROSIE_PARTNER_URL =
  "https://rosieonthehouse.com/listing/durafoam-roofing-inc/";

const episodes = [
  {
    title: "A Guide to Foam Roofing with Durafoam Roofing",
    date: "June 2026 · Rosie on the House",
    body: "Tim Forstie discusses the evolution of foam roofing — thermal insulation, durability, seamless installation, and protective coating against UV exposure — and how foam holds up specifically in Arizona's monsoon season.",
    href: "https://rosieonthehouse.com/podcast/on-the-house-hour-a-guide-to-foam-roofing-with-durafoam-roofing/",
  },
  {
    title:
      "The Particulars of Foam Roofs with Tim Forstie of Durafoam Roofing",
    date: "May 2024 · Rosie on the House",
    body: "Tim walks through Durafoam's company history — starting as Spray Tech, a fishing shop, before becoming Durafoam Roofing — the two-part iso-and-resin foam application process, and why Durafoam doesn't recommend more than 4 inches of foam on a single application.",
    href: "https://rosieonthehouse.com/podcast/on-the-house-hour-the-particulars-of-foam-roofs-with-tim-forstie-of-durafoam-roofing/",
  },
];

const timQuotes: Array<{ quote: string; context: string }> = [
  {
    quote: "The primary reason to use foam is the insulation factor it provides.",
    context: "on why foam outperforms other roofing materials",
  },
  {
    quote:
      "Many of the old roofs we do can be recovered with foam. We clean and prepare the existing membrane and then install the Durafoam system over the top. The process saves on costs for the homeowner and helps the environment by keeping old roofing materials out of our landfills.",
    context: "on foam-over-existing-roof installs",
  },
  {
    quote:
      "We recommend that homeowners have their roofs inspected annually to catch potential problems before they can cause costly repairs.",
    context: "on roof maintenance",
  },
  {
    quote:
      "Recoating can extend roof life while restoring waterproofing and reflectivity.",
    context: "on when to recoat vs. replace",
  },
];

const faqs: Array<{ q: string; a: string }> = [
  {
    q: "What is Rosie on the House?",
    a: "Rosie on the House is Arizona's longest-running home-improvement media resource, broadcasting a Saturday radio show on KTAR 620, a video podcast, and homeowner resources at rosieonthehouse.com. Rosie Romero has been helping Arizona homeowners find contractors they can trust for decades.",
  },
  {
    q: "What does it mean to be a Rosie on the House Certified Partner?",
    a: "Rosie on the House certifies a limited number of contractors in each trade after vetting their work directly — it isn't a paid directory listing. Certified Partners are the contractors Rosie Romero recommends by name to her own radio and podcast audience.",
  },
  {
    q: "How long has Durafoam been a Rosie on the House Certified Partner?",
    a: "Since 1996 — nearly 30 years, making it one of the longest-standing partnerships in the Rosie on the House network.",
  },
  {
    q: "Why does Durafoam keep appearing on Rosie on the House?",
    a: "Rosie on the House regularly brings Tim Forstie on as the foam-roofing expert for the show, because foam is a specialized system many general roofers don't fully understand. Tim has covered foam roofing fundamentals, maintenance, recoating, and Arizona-specific installation challenges across multiple appearances.",
  },
  {
    q: "Where can I hear or read more?",
    a: "Every episode and article is linked above, or you can browse Durafoam's full partner profile directly at rosieonthehouse.com/partner/durafoam-roofing-inc.",
  },
];

const facts = [
  "Certified Partner since 1996 — nearly 30 years, one of the longest-tenured relationships in the Rosie on the House network.",
  "Two full radio/podcast episodes featuring Tim Forstie: ON THE HOUSE HOUR: The Particulars of Foam Roofs (May 2024) and ON THE HOUSE HOUR: A Guide to Foam Roofing (June 2026).",
  "Three written features on rosieonthehouse.com quoting Tim directly: Fantastic Features of Foam Roofing, Roofing Maintenance: Before Small Problems Become Big Ones, and What Are the Benefits of a Foam Roof?",
  "Syndicated newspaper coverage — Tim's foam-roofing expertise, sourced through the Rosie on the House network, was picked up by Tucson.com.",
  "A direct, on-the-record recommendation from Rosie Romero herself.",
];

const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${URL}#aboutpage`,
  url: URL,
  name: TITLE,
  description: DESCRIPTION,
  mainEntity: { "@id": `${SITE_URL}/#business` },
  about: {
    "@type": "Organization",
    name: "Rosie on the House",
    url: "https://www.rosieonthehouse.com",
    sameAs: ["https://www.rosieonthehouse.com/"],
  },
  mentions: [
    {
      "@type": "RadioSeries",
      name: "Rosie on the House",
      url: "https://www.rosieonthehouse.com",
    },
    ...episodes.map((e) => ({
      "@type": "PodcastEpisode",
      name: e.title,
      url: e.href,
      partOfSeries: {
        "@type": "PodcastSeries",
        name: "Rosie on the House",
        url: "https://www.rosieonthehouse.com/podcast/",
      },
    })),
  ],
};

const orgWithAward = businessNode({
  url: PATH,
  aggregateRating: { ratingValue: "4.8", reviewCount: "84" },
});
(orgWithAward as Record<string, unknown>).award =
  "Rosie on the House Certified Partner since 1996";

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const Route = createFileRoute("/rosie-on-the-house")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImageMeta("rosie-on-the-house"),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(orgWithAward) },
      { type: "application/ld+json", children: JSON.stringify(aboutPageSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
    ],
  }),
  component: RosieHubPage,
});

function RosieHubPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative text-white bg-primary">
        <Container className="py-16 lg:py-24">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85">
            Rosie on the House Certified Partner Since 1996
          </p>
          <h1 className="mt-3 max-w-4xl text-3xl font-bold leading-[1.15] text-white sm:text-4xl lg:text-5xl">
            Durafoam has been Rosie on the House's go-to for foam roofing since 1996.
          </h1>
          <p className="mt-5 max-w-3xl text-base text-white/90 sm:text-lg">
            Rosie on the House certifies only the contractors Arizona homeowners can trust — and for nearly 30 years, Durafoam has been their answer for foam roofing questions on the radio, in print, and now on video.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Schedule a Free Inspection
            </ButtonLink>
            <ButtonLink href="#episodes" variant="ghost-light" size="lg">
              Listen to the Latest Episode
            </ButtonLink>
          </div>
        </Container>
      </section>

      {/* Summary */}
      <section className="bg-background py-14 lg:py-16">
        <Container>
          <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-white p-7 shadow-sm">
            <div className="flex items-center gap-3 text-accent">
              <Award className="h-5 w-5" aria-hidden="true" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">
                The story in one paragraph
              </span>
            </div>
            <p className="mt-4 text-[17px] leading-relaxed text-foreground/85">
              Durafoam Roofing has been a Rosie on the House Certified Partner since 1996 — one of the longest-standing contractor relationships in the network. Rosie on the House is Arizona's most trusted home-improvement resource, reaching homeowners through a Saturday radio broadcast on KTAR 620, a video podcast, and rosieonthehouse.com. Owner Tim Forstie has appeared on the show multiple times to explain foam roofing to Arizona homeowners, most recently in a June 2026 episode, <em>"A Guide to Foam Roofing with Durafoam Roofing."</em> Rosie on the House certifies only contractors it has independently vetted — the certification isn't paid advertising, it's a standing recommendation Rosie Romero makes to her own listeners.
            </p>
          </div>
        </Container>
      </section>

      {/* The facts */}
      <section className="bg-[var(--surface)] py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-primary">The facts</h2>
            <ul className="mt-8 space-y-4">
              {facts.map((f, i) => (
                <li key={i} className="flex gap-3 rounded-xl border border-border bg-white p-5">
                  <span className="mt-0.5 grid h-7 w-7 flex-shrink-0 place-items-center rounded-md bg-[var(--surface)] text-accent">
                    <Newspaper className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <p className="text-[15px] leading-relaxed text-foreground/85">{f}</p>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* Rosie pull quote */}
      <section className="bg-background py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-primary">
              What Rosie Romero says about Durafoam
            </h2>
            <figure className="mt-8 rounded-2xl border-l-4 border-accent bg-white p-7 shadow-sm">
              <Quote className="h-8 w-8 text-accent/40" aria-hidden="true" />
              <blockquote className="mt-3 text-[18px] leading-relaxed text-foreground/90 italic">
                "We have worked with Durafoam for almost 20 years. In all that time, we have found them to do excellent work, competitive, trusting and with a high quality standard in their work. We refer them on a regular basis to our radio listeners. The comments back are always good."
              </blockquote>
              <figcaption className="mt-4 text-sm font-semibold text-primary">
                — Rosie Romero, rosieonthehouse.com / KTAR 620
              </figcaption>
            </figure>
          </div>
        </Container>
      </section>

      {/* Episodes */}
      <section id="episodes" className="bg-[var(--surface)] py-16 lg:py-20 scroll-mt-24">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-primary">Listen and watch</h2>
            <p className="mt-3 text-[var(--muted-foreground)]">
              Full episodes featuring Tim Forstie on Rosie on the House.
            </p>

            <div className="mt-10 space-y-6">
              {episodes.map((ep) => (
                <article
                  key={ep.href}
                  className="rounded-2xl border border-border bg-white p-6 lg:p-7"
                >
                  <div className="flex items-center gap-3 text-accent">
                    <Radio className="h-5 w-5" aria-hidden="true" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">
                      {ep.date}
                    </span>
                  </div>
                  <h3 className="mt-3 text-xl font-bold text-primary sm:text-2xl">
                    {ep.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-foreground/85">
                    {ep.body}
                  </p>
                  <a
                    href={ep.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
                  >
                    Listen to the full episode
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </article>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* In Tim's words */}
      <section className="bg-background py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-primary">In Tim's words</h2>
            <p className="mt-3 text-[var(--muted-foreground)]">
              Pulled directly from Durafoam's features on rosieonthehouse.com.
            </p>
            <div className="mt-8 space-y-5">
              {timQuotes.map((q, i) => (
                <figure key={i} className="rounded-xl border border-border bg-white p-6">
                  <blockquote className="text-[16px] leading-relaxed text-foreground/85 italic">
                    "{q.quote}"
                  </blockquote>
                  <figcaption className="mt-3 text-sm text-[var(--muted-foreground)]">
                    — Tim Forstie, {q.context}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-[var(--surface)] py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-primary">Frequently asked questions</h2>
            <div className="mt-8 divide-y divide-border rounded-xl border border-border bg-white">
              {faqs.map((f) => (
                <details key={f.q} className="group p-5">
                  <summary className="cursor-pointer list-none text-lg font-semibold text-primary marker:hidden">
                    {f.q}
                  </summary>
                  <div className="mt-3 text-[15px] leading-relaxed text-foreground/80">
                    {f.q === "Where can I hear or read more?" ? (
                      <>
                        Every episode and article is linked above, or you can browse Durafoam's full partner profile directly at{" "}
                        <a
                          href={ROSIE_PARTNER_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent underline"
                        >
                          rosieonthehouse.com/partner/durafoam-roofing-inc
                        </a>
                        .
                      </>
                    ) : (
                      f.a
                    )}
                  </div>
                </details>
              ))}
            </div>
            <p className="mt-6 text-sm text-[var(--muted-foreground)]">
              More on our story:{" "}
              <Link to="/about-us" className="text-accent underline">
                About Durafoam Roofing
              </Link>{" "}
              ·{" "}
              <Link to="/foam-roofing" className="text-accent underline">
                Foam roofing services
              </Link>
              .
            </p>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="bg-primary py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold text-white sm:text-3xl">
            Ready to work with a Rosie-certified foam roofing specialist?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-white/85">
            Nearly 30 years of trust doesn't happen by accident. Schedule a free, no-pressure inspection and see why Rosie on the House keeps sending Arizona homeowners our way.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Schedule a Free Inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}
